<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import {
		priorityLayoutDevHudPosition,
		setPriorityLayoutDevHudPosition,
	} from 'src/stores/features/dev/priority-layout-dev-hud-state';
	import { Button, Container, IconButton, Text } from '@Shelly/shelly-design-system';

	export let options: { id: string; label: string }[] = [];
	export let selectedId: string;
	export let idPrefix: string;
	export let onSelect: (id: string) => void;
	export let extraOptions: { id: string; label: string }[] = [];
	export let extraSelectedId = '';
	export let extraGroupLabel = '';
	export let onExtraSelect: ((id: string) => void) | undefined;

	const DRAG_THRESHOLD_PX = 3;

	let panelEl: HTMLElement;
	let dragging = false;
	let dragOffsetX = 0;
	let dragOffsetY = 0;
	let dragStartX = 0;
	let dragStartY = 0;
	let dragPointerId: number | undefined;
	let didDrag = false;

	const clampPosition = (x: number, y: number) => {
		const width = panelEl?.offsetWidth ?? 240;
		const height = panelEl?.offsetHeight ?? 120;
		const maxX = Math.max(0, window.innerWidth - width);
		const maxY = Math.max(0, window.innerHeight - height);
		return {
			x: Math.min(maxX, Math.max(0, x)),
			y: Math.min(maxY, Math.max(0, y)),
		};
	};

	const applyPosition = (next: { x: number; y: number } | null) => {
		if (!panelEl) return;
		if (!next) {
			panelEl.style.removeProperty('left');
			panelEl.style.removeProperty('top');
			panelEl.style.removeProperty('bottom');
			return;
		}
		const clamped = clampPosition(next.x, next.y);
		panelEl.style.left = `${clamped.x}px`;
		panelEl.style.top = `${clamped.y}px`;
		panelEl.style.bottom = 'auto';
	};

	const onHandlePointerDown = (event: PointerEvent) => {
		if (event.button !== 0 || !panelEl) return;
		const rect = panelEl.getBoundingClientRect();
		dragOffsetX = event.clientX - rect.left;
		dragOffsetY = event.clientY - rect.top;
		dragStartX = event.clientX;
		dragStartY = event.clientY;
		dragPointerId = event.pointerId;
		dragging = true;
		didDrag = false;
		(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
	};

	const onHandlePointerMove = (event: PointerEvent) => {
		if (!dragging || event.pointerId !== dragPointerId) return;
		if (Math.hypot(event.clientX - dragStartX, event.clientY - dragStartY) > DRAG_THRESHOLD_PX) {
			didDrag = true;
		}
		applyPosition(clampPosition(event.clientX - dragOffsetX, event.clientY - dragOffsetY));
	};

	const onHandlePointerUp = (event: PointerEvent) => {
		if (event.pointerId !== dragPointerId) return;
		if (dragging && panelEl) {
			const rect = panelEl.getBoundingClientRect();
			setPriorityLayoutDevHudPosition(clampPosition(rect.left, rect.top));
		}
		dragging = false;
		dragPointerId = undefined;
	};

	const onHandleClick = (event: MouseEvent) => {
		if (!didDrag) return;
		event.preventDefault();
		event.stopPropagation();
		didDrag = false;
	};

	const onResize = () => {
		const current = get(priorityLayoutDevHudPosition);
		if (!current || !panelEl) return;
		setPriorityLayoutDevHudPosition(clampPosition(current.x, current.y));
	};

	onMount(() => {
		window.addEventListener('resize', onResize);
		const unsub = priorityLayoutDevHudPosition.subscribe((position) => applyPosition(position));
		requestAnimationFrame(() => {
			const current = get(priorityLayoutDevHudPosition);
			if (current) setPriorityLayoutDevHudPosition(clampPosition(current.x, current.y));
			else applyPosition(null);
		});

		return () => {
			window.removeEventListener('resize', onResize);
			unsub();
		};
	});
</script>

<div
	class="priority-layout-dev-hud"
	class:is-dragging={dragging}
	bind:this={panelEl}
	data-prevent-drawer-swipe
	data-prevent-sidepanel-swipe
>
	<Container type="borderSolid" backgroundColor="color-base" padding="s" noScroll>
		<div
			class="d-flex align-items-center gap-xs mb-xs priority-layout-dev-hud-toolbar"
			role="toolbar"
			tabindex="0"
			aria-label={$format('label_dev_emeter_priority_move', {
				default: 'Move layout mode panel',
			})}
			on:pointerdown={onHandlePointerDown}
			on:pointermove={onHandlePointerMove}
			on:pointerup={onHandlePointerUp}
			on:pointercancel={onHandlePointerUp}
		>
			<IconButton
				id={`${idPrefix}-handle`}
				class="priority-layout-dev-hud-handle"
				icon="fas fa-grip-dots-vertical"
				variant="secondary"
				aria-label={$format('label_dev_emeter_priority_move', {
					default: 'Move layout mode panel',
				})}
				on:click={onHandleClick}
			/>
			<Text styleType="miniature">
				{$format('label_dev_emeter_priority_mode', { default: 'Priority layout mode' })}
			</Text>
		</div>
		<div
			class="d-flex gap-xs priority-layout-dev-hud-actions"
			role="group"
			aria-label={$format('label_dev_emeter_priority_mode', { default: 'Priority layout mode' })}
		>
			{#each options as option (option.id)}
				<Button
					id={`${idPrefix}-${option.id}`}
					size="s"
					variant={selectedId === option.id ? 'primary' : 'secondary'}
					aria-pressed={selectedId === option.id}
					aria-label={$format('label_dev_emeter_priority_option', {
						default: option.label,
						values: { option: option.label },
					})}
					on:click={() => onSelect(option.id)}
				>
					{option.label}
				</Button>
			{/each}
		</div>
		{#if extraOptions.length && onExtraSelect}
			<div
				class="d-flex gap-xs mt-xs priority-layout-dev-hud-actions"
				role="group"
				aria-label={extraGroupLabel ||
					$format('label_dev_emeter_priority_mode', { default: 'Priority layout mode' })}
			>
				{#each extraOptions as option (option.id)}
					<Button
						id={`${idPrefix}-extra-${option.id}`}
						size="s"
						variant={extraSelectedId === option.id ? 'primary' : 'secondary'}
						aria-pressed={extraSelectedId === option.id}
						aria-label={$format('label_dev_emeter_priority_option', {
							default: option.label,
							values: { option: option.label },
						})}
						on:click={() => onExtraSelect(option.id)}
					>
						{option.label}
					</Button>
				{/each}
			</div>
		{/if}
	</Container>
</div>

<style>
	.priority-layout-dev-hud {
		position: fixed;
		z-index: 45;
		left: var(--spacing-m);
		bottom: var(--spacing-m);
		width: 240px;
		max-width: calc(100vw - var(--spacing-xl));
	}

	.priority-layout-dev-hud.is-dragging {
		cursor: grabbing;
		user-select: none;
	}

	.priority-layout-dev-hud-actions {
		flex-wrap: wrap;
	}

	.priority-layout-dev-hud :global(.priority-layout-dev-hud-handle),
	.priority-layout-dev-hud-toolbar {
		cursor: grab;
		touch-action: none;
	}

	.priority-layout-dev-hud.is-dragging :global(.priority-layout-dev-hud-handle),
	.priority-layout-dev-hud.is-dragging .priority-layout-dev-hud-toolbar {
		cursor: grabbing;
	}
</style>
